'use client';

import { useState, type CSSProperties } from 'react';

import chartWideAt2xl from '@/components/charts/_shared/chartWideAt2xl.module.css';
import { ChordGraphIsland } from '@/components/charts/ChordGraphIsland';
import { FlywayFilterCarousel } from '@/components/patterns/FlywayFilterCarousel';
import { OutboundTextLink } from '@/components/patterns/OutboundTextLink';
import { GA_CHART_ID, GA_EVENTS, GA_LINK_PLACEMENT, GA_PARAMS } from '@/constants/analytics';
import {
  CHORD_FLYWAY_COLORS,
  CHORD_FLYWAY_FILTERS,
  CHORD_STAT_DISC_DURATION_S,
  CHORD_STAT_DISC_SCALE_FROM,
  CHORD_STAT_DISC_STAGGER_S,
  CHORD_VIEW_MODES,
} from '@/constants/chordGraph';
import { getPrecomputedMostConnected } from '@/content/data/charts/chord-most-connected';
import { useInView } from '@/hooks/useInView';
import { trackEvent } from '@/lib/analytics/trackEvent';

import styles from './ChordGraphFrame.module.css';

import type {
  ChordFlywayFilter,
  ChordViewMode,
} from '@/content/data/charts/chord.types';
import type { Messages } from '@/content/messages';
import type { Locale } from '@/lib/i18n';

type ChordGraphChromeProps = {
  locale: Locale;
  messages: Messages['intro']['chordGraph'];
  chartCaption: string;
};

const MOST_CONNECTED_LISTS = CHORD_FLYWAY_FILTERS.flatMap((filterId) =>
  CHORD_VIEW_MODES.map((viewMode) => ({
    filterId,
    viewMode,
    items: getPrecomputedMostConnected(filterId, viewMode),
  })),
);

function carouselColors(filter: ChordFlywayFilter) {
  if (filter === 'all') {
    return {
      background: 'var(--color-offwhite-2)',
      foreground: 'var(--text-primary)',
    };
  }

  return {
    background: CHORD_FLYWAY_COLORS[filter],
    foreground: 'var(--text-primary)',
  };
}

/**
 * Client chrome for Country Connections: owns view-mode and flyway-filter
 * state so the controls and chart stay in sync.
 * Below `lg`: controls stack (View-by + flyway carousel). At `lg+`: desktop
 * tab column. Also owns the section landmark (`aria-label` from messages.heading).
 */
export function ChordGraphChrome({
  locale,
  messages,
  chartCaption,
}: ChordGraphChromeProps) {
  void locale;
  const [viewMode, setViewMode] = useState<ChordViewMode>('breeding');
  const [flywayFilter, setFlywayFilter] =
    useState<ChordFlywayFilter>('all');
  const { ref: statsRef, inView: statsInView } = useInView<HTMLElement>({
    once: true,
    margin: '0px',
  });

  function handleViewModeChange(next: ChordViewMode) {
    setViewMode(next);
    trackEvent(GA_EVENTS.chartFilter, {
      [GA_PARAMS.chartId]: GA_CHART_ID.chord,
      [GA_PARAMS.flywayId]: flywayFilter,
      [GA_PARAMS.viewMode]: next,
    });
  }

  function handleFlywayFilterChange(next: ChordFlywayFilter) {
    setFlywayFilter(next);
    trackEvent(GA_EVENTS.chartFilter, {
      [GA_PARAMS.chartId]: GA_CHART_ID.chord,
      [GA_PARAMS.flywayId]: next,
      [GA_PARAMS.viewMode]: viewMode,
    });
  }

  // Keep every lightweight precomputed list in the same grid area so neither
  // view-mode nor flyway-filter changes can resize the key and move the
  // vertically centred chart chrome.
  const hasMostConnected = MOST_CONNECTED_LISTS.some(
    ({ filterId, items }) => filterId === flywayFilter && items.length > 0,
  );

  return (
    <section
      className={`${styles.root} ${chartWideAt2xl.wide}`}
      aria-label={messages.heading}
    >
      <div className={styles.chromeScope}>
        {/*
         * Compact: wrap View-by + Show ALL so they use Figma’s 12px gap,
         * not chromeScope’s larger carousel→chart gap (same as State).
         */}
        <div className={styles.controlsStack}>
          <div className={styles.filters}>
            <h3 className={styles.filterHeading}>{messages.heading}</h3>

            <div className={styles.filterGroup}>
              <p className={styles.filterLabel}>{messages.viewByLabel}</p>
              <div className={styles.toggleRow} role="group" aria-label={messages.viewByLabel}>
                <button
                  type="button"
                  className={
                    viewMode === 'breeding' ? styles.toggleActive : styles.toggleInactive
                  }
                  aria-pressed={viewMode === 'breeding'}
                  onClick={() => handleViewModeChange('breeding')}
                >
                  {messages.viewByBreeding}
                </button>
                <button
                  type="button"
                  className={
                    viewMode === 'nonBreeding' ? styles.toggleActive : styles.toggleInactive
                  }
                  aria-pressed={viewMode === 'nonBreeding'}
                  onClick={() => handleViewModeChange('nonBreeding')}
                >
                  {messages.viewByNonBreeding}
                </button>
              </div>
            </div>

            <div className={styles.desktopFilters}>
              <div className={styles.filterGroup}>
                <p className={styles.filterLabel}>{messages.filterByLabel}</p>
                <ul className={styles.filterList} role="group" aria-label={messages.filterByLabel}>
                  {CHORD_FLYWAY_FILTERS.map((filterId, index) => {
                    const label = messages.filters[index] ?? filterId;
                    const isActive = flywayFilter === filterId;
                    return (
                      <li key={filterId}>
                        <button
                          type="button"
                          className={isActive ? styles.filterTabActive : styles.filterTab}
                          aria-pressed={isActive}
                          onClick={() => handleFlywayFilterChange(filterId)}
                        >
                          {label}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <OutboundTextLink href={messages.datazoneUrl} className={styles.datazoneLink} linkPlacement={GA_LINK_PLACEMENT.datazoneChart}>
                {messages.datazoneLink}
              </OutboundTextLink>
            </div>
          </div>

          <FlywayFilterCarousel
            filters={CHORD_FLYWAY_FILTERS}
            value={flywayFilter}
            onChange={handleFlywayFilterChange}
            labels={messages.filters}
            previousLabel={messages.previousFlyway}
            nextLabel={messages.nextFlyway}
            getColors={carouselColors}
            variant="light"
            className={styles.carousel}
          />
        </div>

        <div className={styles.mapColumn}>
          <div className={styles.mapPlate}>
            <ChordGraphIsland
              locale={locale}
              viewMode={viewMode}
              flywayFilter={flywayFilter}
              ribbonGradient={false}
              caption={chartCaption}
            />
          </div>

          {hasMostConnected ? (
            <div className={styles.legend}>
              <p className={styles.legendHeading}>{messages.legendHeading}</p>
              <div className={styles.legendLists}>
                {MOST_CONNECTED_LISTS.map(
                  ({ filterId, viewMode: listViewMode, items }) => {
                    const isActive =
                      flywayFilter === filterId && viewMode === listViewMode;
                    return (
                      <ul
                        key={`${filterId}--${listViewMode}`}
                        className={`${styles.legendList} ${isActive ? '' : styles.legendListInactive}`}
                        aria-hidden={!isActive}
                      >
                        {items.map((item) => (
                          <li key={item.id} className={styles.legendItem}>
                            <span
                              className={styles.swatch}
                              style={{ backgroundColor: item.color }}
                              aria-hidden
                            />
                            <span>{item.label}</span>
                          </li>
                        ))}
                      </ul>
                    );
                  },
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <aside
        ref={statsRef}
        className={styles.stats}
        data-inview={statsInView}
        style={
          {
            '--chord-stat-scale-from': CHORD_STAT_DISC_SCALE_FROM,
            '--chord-stat-stagger': `${CHORD_STAT_DISC_STAGGER_S}s`,
            '--chord-stat-duration': `${CHORD_STAT_DISC_DURATION_S}s`,
          } as CSSProperties
        }
        aria-hidden
      >
        {messages.stats.map((stat, index) => (
          <div
            key={stat.country}
            className={styles.statDisc}
            style={
              {
                '--chord-stat-index': index,
              } as CSSProperties
            }
          >
            {/* eslint-disable-next-line @next/next/no-img-element -- decorative ring SVG (ADR-004) */}
            <img src="/svg/flyways-stat-ring.svg" alt="" className={styles.statRing} />
            <div className={styles.statContent}>
              <p className={styles.statCountry}>{stat.country}</p>
              <p className={styles.statDescription}>
                {stat.description.before}
                <strong>{stat.description.highlight}</strong>
                {stat.description.after}
              </p>
            </div>
          </div>
        ))}
      </aside>
    </section>
  );
}
